import React, { useEffect, useRef } from "react";
import { FaCheck, FaTimes, FaTrash } from "react-icons/fa";

import {
  DEFAULT_AVATAR,
  getAdherentFullName,
  getStatusClass,
  getStatusLabel,
} from "../utils/groupsUtils";

function AdherentStatusModal({
  isOpen,
  adherent,
  group,
  savingAction,
  onApprove,
  onReject,
  onDelete,
  onClose,
}) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/70 px-3 backdrop-blur-sm sm:items-center sm:px-4">
      <div className="relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-[2rem] border border-white/20 bg-white shadow-2xl dark:bg-slate-900 sm:rounded-[2rem]">
        <div className="border-b border-slate-200 bg-gradient-to-br from-slate-950 to-sky-700 p-5 text-white dark:border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/65">
                Gestion inscription
              </p>

              <h2 className="mt-2 font-titre text-2xl font-bold">
                {getAdherentFullName(adherent)}
              </h2>

              <p className="mt-2 text-sm font-medium text-white/70">
                Groupe : {group?.nom || "Non renseigné"}
              </p>
            </div>

            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="flex items-center gap-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
            <img
              src={adherent?.photo_url || DEFAULT_AVATAR}
              alt={getAdherentFullName(adherent)}
              className="h-16 w-16 rounded-2xl object-cover"
              onError={(event) => {
                event.currentTarget.src = DEFAULT_AVATAR;
              }}
            />

            <div>
              <p className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                {getAdherentFullName(adherent)}
              </p>

              <span
                className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(
                  adherent?.status,
                )}`}
              >
                {getStatusLabel(adherent?.status)}
              </span>
            </div>
          </div>

          <div className="mt-5 grid gap-2">
            <button
              type="button"
              onClick={onApprove}
              disabled={savingAction === "approve-adherent"}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              <FaCheck />
              {savingAction === "approve-adherent" ? "Validation..." : "Inscrire"}
            </button>

            <button
              type="button"
              onClick={onReject}
              disabled={savingAction === "reject-adherent"}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-amber-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              <FaTimes />
              {savingAction === "reject-adherent" ? "Refus..." : "Refuser"}
            </button>

            <button
              type="button"
              onClick={onDelete}
              disabled={savingAction === "delete-adherent"}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              <FaTrash />
              {savingAction === "delete-adherent"
                ? "Suppression..."
                : "Supprimer du groupe"}
            </button>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdherentStatusModal;